// "How do narrative standards work?" — button + explainer popup, adapted from Botfield for the
// Billing Review drill-down: only the client scope is editable here, and edits are staged for
// Billing Team approval. Rendered through a portal above any open modal (see narratives.css).

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../../../components/Modal';
import { useEscLayer } from './narrativeLayers';
import './narratives.css';

export default function NarrativeStandardsHelp({ small = false }) {
  const [open, setOpen] = useState(false);
  useEscLayer(open, () => setOpen(false));

  return (
    <>
      <button
        type="button"
        className={`nsx-help-btn${small ? ' nsx-help-btn--sm' : ''}`}
        onClick={e => { e.preventDefault(); setOpen(true); }}
        title="How do narrative standards work?"
      >
        <i className="fas fa-question-circle" /> How do narrative standards work?
      </button>

      {open && createPortal(
        <div className="nsx-stack nsx-stack--help">
          <Modal
            open
            onClose={() => setOpen(false)}
            title="How narrative standards work"
            wide
            footer={<button type="button" className="cs-btn" onClick={() => setOpen(false)}>Got it</button>}
          >
            <div className="nsx-help">
              <p>
                A <strong>narrative standard</strong> sets the wording that appears on a draft's WIP line.
                Standards exist at <strong>three scopes</strong> and <strong>two levels</strong>; when more
                than one could apply to the same WIP, the <strong>most specific one wins</strong>.
              </p>

              <div className="nsx-help-cols">
                <div className="nsx-help-card">
                  <div className="nsx-help-card-title">Scopes</div>
                  <ul>
                    <li><strong>Client</strong> — applies to this one client. <em>This is the only scope you edit here.</em></li>
                    <li><strong>Staff</strong> — applies when a person is the draft's Originator, Partner, or Manager.</li>
                    <li><strong>Global</strong> — the firm-wide default.</li>
                  </ul>
                </div>
                <div className="nsx-help-card">
                  <div className="nsx-help-card-title">Levels</div>
                  <ul>
                    <li><strong>Job</strong> — one or more specific jobs.</li>
                    <li><strong>Service</strong> — an entire service line (e.g. ATTEST).</li>
                  </ul>
                  <div className="nsx-help-card-title nsx-help-card-title--gap">Type</div>
                  <ul>
                    <li><strong>TIME</strong> / <strong>DISB</strong>, or <strong>ALL</strong> (covers both).</li>
                  </ul>
                </div>
              </div>

              <div className="nsx-help-section">Order of precedence (highest to lowest)</div>
              <ol className="nsx-help-ladder">
                <li><span className="nsx-tier nsx-tier--client">Client</span> + Job</li>
                <li><span className="nsx-tier nsx-tier--client">Client</span> + Service</li>
                <li><span className="nsx-tier nsx-tier--staff">Staff</span> + Job</li>
                <li><span className="nsx-tier nsx-tier--staff">Staff</span> + Service</li>
                <li><span className="nsx-tier nsx-tier--global">Global</span> + Job</li>
                <li><span className="nsx-tier nsx-tier--global">Global</span> + Service</li>
              </ol>
              <ul className="nsx-help-notes">
                <li><strong>Scope beats level</strong> — a Client + Service standard outranks a Staff + Job standard.</li>
                <li>A <strong>Job</strong> standard supersedes <em>all</em> lower-precedence <strong>Service</strong> standards (and a Service standard supersedes all lower-precedence Job standards). The "Overwriting" badge in the editor tells you when your standard takes precedence over others.</li>
                <li>Only one standard can cover the same job (or service) with an overlapping type — <strong>ALL</strong> overlaps <strong>TIME</strong> and <strong>DISB</strong>.</li>
                <li>On a <strong>billing-group parent</strong>, "Apply to all billing group" pushes a standard down to every child. Children show it as <strong>Inherited</strong> and can't override it.</li>
              </ul>

              <div className="nsx-help-section">Your edits need approval</div>
              <div className="nsx-help-example">
                Adding, editing or deleting a standard here doesn't change billing right away. Each change is sent to the
                <strong> Billing Team for approval</strong> and shows as <strong>Pending approval</strong> until then (you can
                <em> Withdraw</em> it). Editing or deleting a row that is already pending replaces the older request.
                Once you've signed off this client's review, standards are read-only.
              </div>

              <div className="nsx-help-section">Example</div>
              <div className="nsx-help-example">
                For this client you set a <strong>Client · Job</strong> narrative on their Audit job. That wording wins
                over any staff or global standard for that job — and over any service-level standard too.
              </div>
            </div>
          </Modal>
        </div>,
        document.body,
      )}
    </>
  );
}
